import { Inject, Injectable } from '@angular/core';
import { v4 } from 'uuid';

import { MotionMasterConnectionConfig } from '@oblac/motion-master-client';
import { MotionMasterService } from './motion-master.service';
import { LRSI_WORKER_FACTORY } from './injection-tokens';

@Injectable({
  providedIn: 'root',
})
export class MotionMasterConnectionService {
  private readonly storageKey = 'connectionConfig';

  config: MotionMasterConnectionConfig | null = null;

  constructor(
    private mm: MotionMasterService,
    @Inject(LRSI_WORKER_FACTORY) readonly lrsiWorkerFactory: (config: MotionMasterConnectionConfig) => Worker
  ) {}

  hydrateConnection() {
    const item = localStorage.getItem(this.storageKey);
    if (item) {
      const config = JSON.parse(item) as MotionMasterConnectionConfig;
      config.clientId = v4().slice(0, 8);
      this.connect(config);
    }
  }

  connect(config: MotionMasterConnectionConfig) {
    const protocol = config.secure ? 'wss' : 'ws';

    this.mm.client.request.socket.open(
      `${protocol}://${config.hostname}:${config.reqResPort}?clientId=${config.clientId}`,
      config.pingSystemInterval,
      config.systemAliveTimeout
    );

    this.mm.client.monitor.socket.open(
      `${protocol}://${config.hostname}:${config.pubSubPort}?clientId=${config.clientId}`
    );

    if (config.clientAliveTimeout) {
      this.mm.client
        .whenReady(5000)
        .then(() => {
          this.mm.client.request.setSystemClientTimeout({ timeoutMs: config.clientAliveTimeout });
        })
        .catch((err) => console.error(`Failed to connect to Motion Master: ${err} ${JSON.stringify(config)}`));
    }

    if (config.remember) {
      localStorage.setItem(this.storageKey, JSON.stringify(config));
    } else {
      localStorage.removeItem(this.storageKey);
    }

    this.config = config;
  }

  disconnect(forget = false) {
    this.mm.client.closeSockets();

    if (forget) {
      localStorage.removeItem(this.storageKey);
    }
  }

  /**
   * Creates and initializes a new Web Worker dedicated to running the Limited Range System Identification (LRSI) procedure.
   *
   * This method defensively clones the existing connection configuration and assigns a new, unique Client ID
   * to the worker, ensuring the worker's socket connection is distinct from the main application's socket.
   *
   * @returns A new Worker instance. The caller must listen to the worker's 'onmessage' events
   * to monitor connection status and procedure progress.
   * @throws {Error} If `this.config` is null (i.e., the Motion Master client is not configured).
   */
  createLrsiWorker(): Worker {
    if (!this.config) {
      throw new Error('No connection config available to create LRSI worker.');
    }

    // Create a new, unique clientId for the worker
    const config = { ...this.config, clientId: 'lrsi-worker-' + v4().slice(0, 8) };

    return this.lrsiWorkerFactory(config);
  }
}
